import React from "react";
import { DialogContainer } from "react-md";

// Formats a number as "Rs 1,700" (Indian grouping).
const formatRs = (n) => {
  const num = Number(n || 0);
  return "Rs " + Math.abs(num).toLocaleString("en-IN");
};

// Colour tokens for the two payment directions.
const GREEN = "#1b873f"; // additional payment to collect
const GREEN_BG = "#e7f6ec";
const RED = "#d14343"; // refund to the user
const RED_BG = "#fdecea";
const NEUTRAL = "#888";

/**
 * Payment-details preview for a cart under verification (VERIFY mode).
 *
 * Driven entirely by the `/api/v1/cart/admin/{cartId}/payment-sublimit` response
 * (`data`). Shows what the patient already paid vs. what is now applicable after
 * the bridge user's edits, the per-channel CHANGE (collect = green / refund =
 * red), and the resulting wallet/Razorpay action. FIRST dialog of the cart
 * payment flow; "Continue" hands off (onContinue) to the approval step.
 */
const CartPaymentDetailsDialog = (props) => {
  const { visible, onClose, data, onContinue, loading = false } = props;

  if (!data) return null;

  const paid = data.paidComposition || { wallet: 0, external: 0, total: 0 };
  const target = data.targetComposition || { wallet: 0, external: 0, total: 0 };
  const grandTotal = data.newTotal != null ? data.newTotal : target.total;

  const paidTotal = paid.total != null ? paid.total : paid.wallet + paid.external;
  const targetTotal =
    target.total != null ? target.total : target.wallet + target.external;

  // Change = applicable − already paid, per channel. Positive ⇒ collect more
  // (green), negative ⇒ refund to the user (red).
  const change = {
    wallet: (target.wallet || 0) - (paid.wallet || 0),
    external: (target.external || 0) - (paid.external || 0),
    total: targetTotal - paidTotal,
  };
  const netTotal = change.total;
  const isAdditional = netTotal > 0;
  const isRefund = netTotal < 0;

  // Per-channel action lines (icon + colour). Prefer the explicit delta
  // compositions from the API; fall back to the computed change otherwise.
  const newPay = data.newPayment ? data.newPayment.deltaComposition : null;
  const refund = data.refund ? data.refund.deltaComposition : null;

  const modifyLines = [];
  if (newPay || refund) {
    if (newPay?.wallet > 0)
      modifyLines.push({ label: "Payment via Wallet", amount: newPay.wallet, channel: "wallet", dir: "pay" });
    if (newPay?.external > 0)
      modifyLines.push({ label: "Payment via Razorpay", amount: newPay.external, channel: "external", dir: "pay" });
    if (refund?.wallet > 0)
      modifyLines.push({ label: "Refund to Wallet", amount: refund.wallet, channel: "wallet", dir: "refund" });
    if (refund?.external > 0)
      modifyLines.push({ label: "Refund to Razorpay/Source", amount: refund.external, channel: "external", dir: "refund" });
  } else {
    if (change.wallet !== 0)
      modifyLines.push({
        label: change.wallet > 0 ? "Payment via Wallet" : "Refund to Wallet",
        amount: change.wallet,
        channel: "wallet",
        dir: change.wallet > 0 ? "pay" : "refund",
      });
    if (change.external !== 0)
      modifyLines.push({
        label: change.external > 0 ? "Payment via Razorpay" : "Refund to Razorpay/Source",
        amount: change.external,
        channel: "external",
        dir: change.external > 0 ? "pay" : "refund",
      });
  }

  // Net-action summary banner content.
  const summary = isAdditional
    ? {
        color: GREEN,
        bg: GREEN_BG,
        icon: "arrow_upward",
        title: `Collect ${formatRs(netTotal)} additional payment`,
      }
    : isRefund
      ? {
          color: RED,
          bg: RED_BG,
          icon: "arrow_downward",
          title: `Refund ${formatRs(netTotal)} to the user`,
        }
      : {
          color: GREEN,
          bg: GREEN_BG,
          icon: "check_circle",
          title: "Payment already settled — no change required",
        };

  // ---- Shared cell styles ----
  const cellStyle = {
    border: "1px solid #ececf0",
    padding: "14px 12px",
    textAlign: "center",
    fontSize: "15px",
  };
  const headCellStyle = {
    ...cellStyle,
    background: "#f4f4f6",
    color: "#666",
    fontWeight: 600,
  };
  const rowLabelStyle = { ...cellStyle, textAlign: "left", color: "#555" };

  const icon = (name, style) => (
    <i className="material-icons" style={{ lineHeight: 1, ...style }}>
      {name}
    </i>
  );

  const channelIcon = (channel) =>
    channel === "wallet" ? "account_balance_wallet" : "credit_card";

  // A "Change in amount" cell: signed, coloured, with a direction arrow.
  const changeCell = (value) => {
    const v = Number(value || 0);
    if (v === 0) {
      return (
        <td style={{ ...cellStyle, color: NEUTRAL, background: "#fcfcfd" }}>
          Rs 0
        </td>
      );
    }
    const up = v > 0;
    return (
      <td
        style={{
          ...cellStyle,
          color: up ? GREEN : RED,
          fontWeight: 700,
          background: up ? GREEN_BG : RED_BG,
        }}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "3px",
          }}
        >
          {icon(up ? "arrow_upward" : "arrow_downward", { fontSize: "16px" })}
          {formatRs(v)}
        </span>
      </td>
    );
  };

  // Column header with a small channel icon.
  const channelHead = (label, channel) => (
    <th style={headCellStyle}>
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "4px",
        }}
      >
        {icon(channelIcon(channel), { fontSize: "16px", color: "#888" })}
        {label}
      </span>
    </th>
  );

  return (
    <DialogContainer
      id="cart-payment-details-dialog"
      visible={visible}
      focusOnMount={false}
      onHide={onClose}
      width={"640px"}
      autosizeContent={true}
      style={{ maxHeight: "100vh", overflow: "auto" }}
      dialogStyle={{ borderRadius: "12px" }}
    >
      <div style={{ padding: "8px 24px 24px" }}>
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            marginBottom: "18px",
          }}
        >
          <h2 style={{ margin: 0, fontWeight: 700 }}>Payment details</h2>
          <span
            onClick={onClose}
            style={{
              position: "absolute",
              right: 0,
              top: "-4px",
              cursor: "pointer",
              fontSize: "34px",
              lineHeight: 1,
              color: "#333",
            }}
          >
            &times;
          </span>
        </div>

        {/* Grand total chip */}
        <div style={{ textAlign: "center", marginBottom: "22px" }}>
          <span
            style={{
              display: "inline-block",
              background: "#ecebfb",
              borderRadius: "8px",
              padding: "10px 28px",
              fontSize: "16px",
              color: "#333",
            }}
          >
            Grand total: <strong>{formatRs(grandTotal)}</strong>
          </span>
        </div>

        {/* Composition table */}
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            marginBottom: "22px",
          }}
        >
          <thead>
            <tr>
              <th style={headCellStyle}></th>
              {channelHead("Wallet", "wallet")}
              {channelHead("Razorpay", "external")}
              <th style={headCellStyle}>Total</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={rowLabelStyle}>Already paid</td>
              <td style={cellStyle}>{formatRs(paid.wallet)}</td>
              <td style={cellStyle}>{formatRs(paid.external)}</td>
              <td style={cellStyle}>{formatRs(paidTotal)}</td>
            </tr>
            <tr>
              <td style={{ ...rowLabelStyle, fontWeight: 600 }}>
                Change in amount
              </td>
              {changeCell(change.wallet)}
              {changeCell(change.external)}
              {changeCell(change.total)}
            </tr>
            <tr>
              <td style={{ ...rowLabelStyle, fontWeight: 700, color: "#333" }}>
                Applicable payment
              </td>
              <td style={{ ...cellStyle, fontWeight: 700 }}>
                {formatRs(target.wallet)}
              </td>
              <td style={{ ...cellStyle, fontWeight: 700 }}>
                {formatRs(target.external)}
              </td>
              <td style={{ ...cellStyle, fontWeight: 700 }}>
                {formatRs(targetTotal)}
              </td>
            </tr>
          </tbody>
        </table>

        {/* Net-action summary + per-channel breakdown */}
        <div
          style={{
            display: "flex",
            gap: "12px",
            alignItems: "flex-start",
            background: summary.bg,
            borderRadius: "10px",
            padding: "14px 16px",
            marginBottom: "20px",
          }}
        >
          <span
            style={{
              width: "34px",
              height: "34px",
              borderRadius: "50%",
              background: summary.color,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {icon(summary.icon, { color: "#fff", fontSize: "20px" })}
          </span>
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontWeight: 700,
                color: summary.color,
                fontSize: "15px",
              }}
            >
              {summary.title}
            </div>
            {modifyLines.map((line, idx) => {
              const lineColor = line.dir === "pay" ? GREEN : RED;
              return (
                <div
                  key={idx}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    color: "#555",
                    fontSize: "13.5px",
                    marginTop: "6px",
                  }}
                >
                  {icon(channelIcon(line.channel), {
                    fontSize: "16px",
                    color: lineColor,
                  })}
                  <span>{line.label}</span>
                  <strong style={{ color: lineColor, marginLeft: "auto" }}>
                    {line.dir === "pay" ? "+" : "−"}
                    {formatRs(line.amount)}
                  </strong>
                </div>
              );
            })}
          </div>
        </div>

        {/* Note */}
        {data.message && (
          <div
            style={{
              display: "flex",
              gap: "10px",
              alignItems: "flex-start",
              background: "#fdeaea",
              borderRadius: "8px",
              padding: "14px 18px",
              color: "#333",
              marginBottom: "26px",
            }}
          >
            {icon("info", { color: "#d14343", fontSize: "20px", flexShrink: 0 })}
            <div>
              <strong>Note:</strong> {data.message}
            </div>
          </div>
        )}

        {/* Continue */}
        <div style={{ textAlign: "center" }}>
          <button
            onClick={onContinue}
            disabled={loading}
            style={{
              background: loading ? "#f0b27a" : "#ee7e2c",
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              padding: "14px 60px",
              fontSize: "16px",
              fontWeight: 600,
              cursor: loading ? "not-allowed" : "pointer",
            }}
          >
            {loading ? "Please wait..." : "Continue"}
          </button>
        </div>
      </div>
    </DialogContainer>
  );
};

export default CartPaymentDetailsDialog;
